import { useState, useEffect, useRef } from 'react'
import { useAudioNoteStartTimeMs, useIsAudioNotePaused } from './use-audio-note-selectors'

/**
 * Formats milliseconds into MM:SS or HH:MM:SS format
 */
const formatTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Hook that returns the elapsed time since the audio note started.
 * Updates every second to show the current elapsed time.
 * Properly tracks paused duration.
 */
export const useAudioElapsedTime = () => {
  // Selector already yields an epoch-ms timestamp for stable dependency comparison
  const startTimeMs = useAudioNoteStartTimeMs()
  const isPaused = useIsAudioNotePaused()
  const [elapsedTime, setElapsedTime] = useState<string>('00:00')

  // Track pause state
  const pausedAtRef = useRef<number | null>(null)
  const totalPausedMsRef = useRef<number>(0)

  // Reset when startTime changes (new recording)
  useEffect(() => {
    pausedAtRef.current = null
    totalPausedMsRef.current = 0
  }, [startTimeMs])

  // Track pause/resume in effect, not render phase
  useEffect(() => {
    if (isPaused) {
      if (pausedAtRef.current === null) {
        pausedAtRef.current = Date.now()
      }
    } else {
      if (pausedAtRef.current !== null) {
        totalPausedMsRef.current += Date.now() - pausedAtRef.current
        pausedAtRef.current = null
      }
    }
  }, [isPaused])

  useEffect(() => {
    if (startTimeMs === null) {
      setElapsedTime('00:00')
      return
    }

    const updateElapsedTime = () => {
      const now = Date.now()
      let elapsed = now - startTimeMs - totalPausedMsRef.current

      // If currently paused, subtract the current pause duration too
      if (pausedAtRef.current !== null) {
        elapsed -= now - pausedAtRef.current
      }

      setElapsedTime(formatTime(Math.max(0, elapsed)))
    }

    updateElapsedTime()

    // Only run interval if not paused
    if (!isPaused) {
      const interval = setInterval(updateElapsedTime, 1000)
      return () => clearInterval(interval)
    }
  }, [startTimeMs, isPaused])

  return elapsedTime
}
